/* eslint-disable react-hooks/exhaustive-deps */
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useEffect, useRef, useState } from "react";
import { TomraDropsTable } from "./TomraDropsTable";
import { FormSelect, PrimaryButton, SearchInput } from "../../components";
import {
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import { useTomraDrops } from "../../store/useTomraDrops";
import { useDropLocations } from "../../store/useDropLocations";
import { useAppLoading } from "../../store/useAppLoading";

let searchTimeout: string | number | NodeJS.Timeout | null | undefined = null;

function TomraDropsScreen() {
  /** Hooks */
  const {
    downloadTomraDropsInCSV,
    tomraDrops,
    tomraDropsLoading,
    tomraDropsInitialized,
    getTomraDrops,
    searchText,
    setSearchText,
    clearSearchText,
    hasNextPage,
    highlightedStatus,
    setHighlightedStatus,
    tomraDropsLocationFilter,
    setTomraDropsLocationFilter,
    setTomraDropsInitialized,
  } = useTomraDrops();
  const { dropLocations, getDropLocations } = useDropLocations();
  const { isLoading } = useAppLoading();
  const didMountRef = useRef(false);
  const highlightedStatusEffectRan = useRef(false);
  const initialFetchDone = useRef(false);
  /** State */
  const [page, setPage] = useState(1);

  /** Effects – initial load: run only once per mount */
  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;
    getDropLocations();
    setTomraDropsInitialized(false);
    getTomraDrops(1, 20, "");
    return () => {
      clearSearchText();
      setHighlightedStatus(null);
    };
  }, []);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const query = (searchText || "").trim();
    if (query === "") {
      // Empty search is handled explicitly in handleSearch to avoid loops
      return;
    }
    if (searchTimeout) clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      setPage(1);
      setHighlightedStatus(null);
      getTomraDrops(1, 20, query);
    }, 500);
    return () => {
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, [searchText]);

  useEffect(() => {
    if (!highlightedStatusEffectRan.current) {
      highlightedStatusEffectRan.current = true;
      return;
    }
    getTomraDrops(page, 20, searchText || "");
  }, [highlightedStatus]);

  /** Callbacks */
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);

    // When clearing the search box, immediately reset to unfiltered list once
    const query = value.trim();
    if (query === "") {
      if (searchTimeout) clearTimeout(searchTimeout);
      setPage(1);
      setHighlightedStatus(null);
      getTomraDrops(1, 20, "");
    }
  };
  const handleLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setTomraDropsLocationFilter(value);
    setPage(1);
    getTomraDrops(1, 20, searchText || undefined);
  };

  return (
    <div>
      <div className="flex w-full justify-between items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Statiegeld Drops
        </h1>
        <div className="flex flex-col sm:flex-row">
          <Menu>
            <MenuHandler>
              <button className="h-10 !bg-droppie-oranje !border-droppie-oranje  mr-4 shrink-0 text-white rounded-[4px] font-medium h-8 px-4 py-0 flex items-center justify-center">
                {highlightedStatus || "Highlight status"}
                <ChevronDown className="ml-2  w-4 *:fill-white stroke-white fill-white" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              <MenuItem
                onClick={() => setHighlightedStatus("Approved")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Approved
              </MenuItem>
              <MenuItem
                onClick={() => setHighlightedStatus("Pending")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Pending
              </MenuItem>
              <MenuItem
                onClick={() => setHighlightedStatus("Declined")}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Declined
              </MenuItem>
            </MenuList>
          </Menu>

          <PrimaryButton
            onClick={downloadTomraDropsInCSV}
            label="Download in CSV"
            size="sm"
            className="h-10 !bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
          />
        </div>
      </div>

      <section className="w-full px-7 py-3 bg-white rounded-xl shadow-none mb-3 flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="w-full sm:w-56 shrink-0">
          <FormSelect
            label=""
            value={tomraDropsLocationFilter}
            onChange={handleLocationChange}
            options={[
              { value: "all", label: "All locations" },
              ...(dropLocations?.map((loc) => ({
                value: loc?._id as string,
                label: (loc?.cost_place ?? loc?.name ?? loc?._id) as string,
              })) ?? []),
            ]}
            inputClassName="h-10"
          />
        </div>
        <div className="flex-1 min-w-0">
          <SearchInput
            value={searchText}
            onChange={handleSearch}
            placeholder="Search by drop ID, username, email..."
          />
        </div>
      </section>
      <section className="w-full px-2 sm:px-7 py-2 sm:py-7 bg-white rounded-xl shadow-none drop-shadow-none">
        {!tomraDropsInitialized || isLoading || tomraDropsLoading ? (
          <div className="flex items-center justify-center h-[240px] w-full">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin fill-primary-base"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </div>
        ) : !tomraDrops?.length ? (
          <p className="text-center font-satoshi text-base text-dark">
            There is no statiegeld drop created yet.
          </p>
        ) : (
          <TomraDropsTable />
        )}
      </section>
      {hasNextPage ? (
        <div className="flex flex-row w-full items-center justify-end mt-3">
          <PrimaryButton
            size="sm"
            className="h-10 !bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
            onClick={() => {
              setPage(page + 1);
              if (searchText) {
                getTomraDrops(page + 1, 20, searchText);
              } else {
                getTomraDrops(page + 1, 20);
              }
            }}
            label="Load more"
          />
        </div>
      ) : null}
    </div>
  );
}

export default withProtectedRoute(TomraDropsScreen);
